import { useForm } from '@tanstack/react-form';
import type { FormFooterProps, FormProps } from '@/types/form/form-props';
import { cn } from '@/lib/utils';
import { RegisterInput, RegisterInputSchema } from '../api/register';
import { TextInput } from '@/shared/components/ui/inputs/text-input';
import { fieldToInputProps, toFormProps } from '@/utils/shared/form';
import { Link, useLocation } from 'react-router-dom';
import { LoadingButton } from '@/shared/components/ui/buttons/loading-button';
import { Rocket } from 'lucide-react';
import { paths } from '@/config/paths';
import { Button } from '@/shared/components/ui/button';

export function RegisterForm({
  onSubmit,
  className,
  Footer = RegisterFormFooter,
  defaultValues,
}: FormProps<RegisterInput>) {
  const form = useForm({
    defaultValues: { firstName: '', lastName: '', email: '', mobileNumber: '', ...defaultValues },
    validators: { onSubmit: RegisterInputSchema },
    onSubmit: ({ value }) => onSubmit(value),
  });

  return (
    <form {...toFormProps(form)} className={cn('space-y-4', className)}>
      <div className="flex gap-4">
        <form.Field name="firstName">{(field) => <TextInput {...fieldToInputProps(field, 'Förnamn')} />}</form.Field>
        <form.Field name="lastName">{(field) => <TextInput {...fieldToInputProps(field, 'Efternamn')} />}</form.Field>
      </div>
      <form.Field name="mobileNumber">{(field) => <TextInput {...fieldToInputProps(field, 'Mobil')} />}</form.Field>
      <form.Field name="email">{(field) => <TextInput {...fieldToInputProps(field, 'E-post')} />}</form.Field>

      <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
        {([canSubmit, isSubmitting]) => <Footer canSubmit={canSubmit} isSubmitting={isSubmitting} />}
      </form.Subscribe>
    </form>
  );
}

export function RegisterFormFooter({ canSubmit, isSubmitting, submitText = 'Registera dig' }: FormFooterProps) {
  const { search } = useLocation();

  return (
    <div className="flex gap-2 justify-between">
      <Button asChild variant={'ghost'}>
        <Link to={paths.auth.login.getHref(new URLSearchParams(search).get('redirect'))}>Använt piggo förut?</Link>
      </Button>
      <LoadingButton disabled={!canSubmit} isLoading={isSubmitting} type="submit">
        <Rocket />
        {submitText}
      </LoadingButton>
    </div>
  );
}
